import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { Menu, X, Ship, FileText, Users, Bot, Globe, ChevronDown, Home } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "@/lib/router-compat";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { labelKey: "nav.home", href: "/", icon: Home },
  { labelKey: "nav.solutions", href: "/solutions", icon: Ship },
  { labelKey: "nav.digitalWorkers", href: "/digital-workers", icon: Bot },
  { labelKey: "nav.cases", href: "/customers", icon: FileText },
  
  { labelKey: "nav.about", href: "/about", icon: Users },
];

const languages = [
  { code: "en" as const, label: "English" },
  { code: "th" as const, label: "ภาษาไทย" },
  { code: "zh" as const, label: "简体中文" },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const localize = (path: string) => {
    if (language === "en") return path;
    const prefix = `/${language}`;
    return path === "/" ? prefix : `${prefix}${path}`;
  };
  const aboutCargoLabel =
    language === "zh" ? "关于 CargoNova" : language === "th" ? "เกี่ยวกับ CargoNova" : "About CargoNova";
  const aboutDeepLabel =
    language === "zh" ? "关于 Deep Insights" : language === "th" ? "เกี่ยวกับ Deep Insights" : "About Deep Insights";

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border/60"
    >
      <nav className="container mx-auto px-6 py-3">
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-6">
          {/* Logo */}
          <Link to={localize("/")} className="flex items-center gap-2">
            <svg
              width="28"
              height="28"
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 shrink-0"
            >
              <path
                d="M 60 20 L 65 50 L 95 25 L 70 55 L 100 60 L 70 65 L 95 95 L 65 70 L 60 100 L 55 70 L 25 95 L 50 65 L 20 60 L 50 55 L 25 25 L 55 50 Z"
                fill="#0BBAB1"
              />
              <path
                d="M 60 32 L 63 52 L 83 37 L 68 57 L 88 60 L 68 63 L 83 83 L 63 68 L 60 88 L 57 68 L 37 83 L 52 63 L 32 60 L 52 57 L 37 37 L 57 52 Z"
                fill="#0BBAB1"
                opacity="0.4"
              />
              <line x1="60" y1="60" x2="60" y2="15" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="95" y2="25" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="105" y2="60" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="95" y2="95" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="60" y2="105" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="25" y2="95" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="15" y2="60" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="25" y2="25" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <circle cx="60" cy="60" r="16" fill="#0BBAB1" />
              <path
                d="M 53 67 L 53 53 L 55 53 L 65 63 L 65 53 L 67 53 L 67 67 L 65 67 L 55 57 L 55 67 Z"
                fill="white"
                opacity="0.9"
              />
            </svg>
            <span className="text-xl font-bold text-foreground">
              Cargo<span className="text-gradient-ai">Nova</span>
            </span>
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center gap-8">
            {navItems
              .filter((item) => item.labelKey !== "nav.about" && item.labelKey !== "nav.faq")
              .map((item) => (
                item.href.startsWith('/') ? (
                  <Link
                    key={item.labelKey}
                    to={localize(item.href)}
                    className="text-sm font-medium text-foreground/80 hover:text-secondary transition-colors"
                  >
                    {t(item.labelKey)}
                  </Link>
                ) : (
                  <a
                    key={item.labelKey}
                    href={item.href}
                    className="text-sm font-medium text-foreground/80 hover:text-secondary transition-colors"
                  >
                    {t(item.labelKey)}
                  </a>
                )
              ))}
            <div className="relative group">
              <button className="inline-flex items-center gap-1 text-sm font-medium text-foreground/80 group-hover:text-secondary transition-colors outline-hidden py-2">
                {t("nav.faq")}
                <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 min-w-[220px] opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                <div className="rounded-xl border border-border/60 bg-white/95 backdrop-blur-xl shadow-lg shadow-black/5 p-1.5">
                  <Link
                    to={localize("/what-is-cargonova")}
                    className="block cursor-pointer rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-secondary/10 hover:text-secondary"
                  >
                    {t("nav.faq.whatIs")}
                  </Link>
                  <Link
                    to={localize("/compare/cargonova-vs-cargowise")}
                    className="block cursor-pointer rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-secondary/10 hover:text-secondary"
                  >
                    {t("nav.faq.compare")}
                  </Link>
                  <Link
                    to={localize("/pricing")}
                    className="block cursor-pointer rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-secondary/10 hover:text-secondary"
                  >
                    {t("nav.faq.pricing")}
                  </Link>
                  <Link
                    to={localize("/implementation")}
                    className="block cursor-pointer rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-secondary/10 hover:text-secondary"
                  >
                    {t("nav.faq.implementation")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="relative group">
              <button className="inline-flex items-center gap-1 text-sm font-medium text-foreground/80 group-hover:text-secondary transition-colors outline-hidden py-2">
                {t("nav.about")}
                <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
              </button>
              {/* Hover bridge + dropdown */}
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 min-w-[200px] opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                <div className="rounded-xl border border-border/60 bg-white/95 backdrop-blur-xl shadow-lg shadow-black/5 p-1.5">
                  <Link
                    to={localize("/about")}
                    className="block cursor-pointer rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-secondary/10 hover:text-secondary"
                  >
                    {aboutCargoLabel}
                  </Link>
                  <a
                    href="https://www.deep-insights.ai/about"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block cursor-pointer rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-secondary/10 hover:text-secondary"
                  >
                    {aboutDeepLabel}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button with Language Switcher */}
          <div className="hidden lg:flex items-center gap-3 justify-self-end">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-4 h-10 rounded-full border border-border/70 bg-background hover:border-secondary/60 hover:text-secondary transition-colors">
                  <Globe className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {languages.find((l) => l.code === language)?.label}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? "bg-muted" : ""}
                  >
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <a href="https://www.deep-insights.ai/book-demo" target="_blank" rel="noopener noreferrer">
              <Button
                className="h-10 px-6 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-xs font-medium"
              >
                {t("nav.consult")}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors col-start-3 justify-self-end"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden mt-4 pb-4"
            >
              <div className="flex flex-col gap-2">
                {navItems
                  .filter((item) => item.labelKey !== "nav.about" && item.labelKey !== "nav.faq")
                  .map((item) => (
                    item.href.startsWith('/') ? (
                      <Link
                        key={item.labelKey}
                        to={localize(item.href)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        <item.icon className="w-5 h-5 text-secondary" />
                        <span className="font-medium">{t(item.labelKey)}</span>
                      </Link>
                    ) : (
                      <a
                        key={item.labelKey}
                        href={item.href}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        <item.icon className="w-5 h-5 text-secondary" />
                        <span className="font-medium">{t(item.labelKey)}</span>
                      </a>
                    )
                  ))}
                {/* FAQ submenu for mobile */}
                <div className="px-4 pt-2 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("nav.faq")}
                </div>
                <Link
                  to={localize("/what-is-cargonova")}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <FileText className="w-5 h-5 text-secondary" />
                  <span className="font-medium">{t("nav.faq.whatIs")}</span>
                </Link>
                <Link
                  to={localize("/compare/cargonova-vs-cargowise")}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <FileText className="w-5 h-5 text-secondary" />
                  <span className="font-medium">{t("nav.faq.compare")}</span>
                </Link>
                <Link
                  to={localize("/pricing")}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <FileText className="w-5 h-5 text-secondary" />
                  <span className="font-medium">{t("nav.faq.pricing")}</span>
                </Link>
                <Link
                  to={localize("/implementation")}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <FileText className="w-5 h-5 text-secondary" />
                  <span className="font-medium">{t("nav.faq.implementation")}</span>
                </Link>
                {/* About submenu for mobile */}
                <div className="px-4 pt-2 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("nav.about")}
                </div>
                <Link
                  to={localize("/about")}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <Users className="w-5 h-5 text-secondary" />
                  <span className="font-medium">{aboutCargoLabel}</span>
                </Link>
                <a
                  href="https://www.deep-insights.ai/about"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <Globe className="w-5 h-5 text-secondary" />
                  <span className="font-medium">{aboutDeepLabel}</span>
                </a>
                {/* Language Switcher for Mobile */}
                <div className="flex items-center gap-2 px-4 py-3">
                  <Globe className="w-5 h-5 text-secondary" />
                  <div className="flex gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => setLanguage(lang.code)}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          language === lang.code
                            ? "bg-secondary text-secondary-foreground"
                            : "hover:bg-muted"
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </div>
                <a href="https://www.deep-insights.ai/book-demo" target="_blank" rel="noopener noreferrer">
                  <Button variant="hero" className="mt-4">
                    {t("nav.consult")}
                  </Button>
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
}
